import React from 'react';
import { Link } from 'react-router-dom';
import {
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import styles from './Footer.module.scss';
import packageJson from '../../../package.json';

const FacebookIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M13.5 21v-7h2.4l.4-3h-2.8V9.2c0-.9.3-1.5 1.6-1.5H16V5.1c-.5-.1-1.3-.1-2.1-.1-2.1 0-3.5 1.3-3.5 3.7V11H8v3h2.4v7h3.1Z" />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.9 3H21l-4.6 5.3L22 21h-4.8l-3.8-4.9L9.1 21H7l5-5.7L2.6 3h4.9l3.4 4.5L18.9 3Zm-1.7 16h1.3L6.2 4.9H4.8L17.2 19Z" />
  </svg>
);

const YouTubeIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M21.6 7.2a2.9 2.9 0 0 0-2-2C17.8 4.7 12 4.7 12 4.7s-5.8 0-7.6.5a2.9 2.9 0 0 0-2 2C2 9 2 12 2 12s0 3 .4 4.8a2.9 2.9 0 0 0 2 2c1.8.5 7.6.5 7.6.5s5.8 0 7.6-.5a2.9 2.9 0 0 0 2-2C22 15 22 12 22 12s0-3-.4-4.8ZM10 15.4V8.6l6 3.4-6 3.4Z" />
  </svg>
);

const InstagramIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.8A3.95 3.95 0 0 0 3.8 7.75v8.5a3.95 3.95 0 0 0 3.95 3.95h8.5a3.95 3.95 0 0 0 3.95-3.95v-8.5a3.95 3.95 0 0 0-3.95-3.95h-8.5Zm8.95 1.35a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 1.8A3.2 3.2 0 1 0 12 15.2 3.2 3.2 0 0 0 12 8.8Z" />
  </svg>
);

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const appVersion = `v${packageJson.version}`;

  const footerLinks = {
    program: [
      { name: 'Zakat', href: '/programs?category=zakat' },
      { name: 'Pendidikan', href: '/programs?category=pendidikan' },
      { name: 'Kesehatan', href: '/programs?category=kesehatan' },
      { name: 'Ekonomi', href: '/programs?category=ekonomi' },
      { name: 'Sosial Kemanusiaan', href: '/programs?category=sosial-kemanusiaan' },
      { name: 'Dakwah', href: '/programs?category=dakwah' },
      { name: 'Wakaf Tunai', href: '/programs?category=wakaf-tunai' },
    ],
    company: [
      { name: 'Tentang Kami', href: '/about' },
      { name: 'Dashboard Transparansi', href: '/dashboard' },
      { name: 'Visi & Misi', href: '/vision-mission' },
      { name: 'Struktur Organisasi', href: '/organization' },
      { name: 'Laporan Keuangan', href: '/financial-report' },
      { name: 'Berita & Artikel', href: '/news' },
    ],
    support: [
      { name: 'Cara Donasi', href: '/how-to-donate' },
      { name: 'FAQ', href: '/faq' },
      { name: 'Hubungi Kami', href: '/contact' },
      { name: 'Kebijakan Privasi', href: '/privacy-policy' },
      { name: 'Syarat & Ketentuan', href: '/terms' },
    ],
  };

  const socialLinks = [
    { name: 'Facebook', icon: FacebookIcon, href: 'https://facebook.com/lazismu-ummi' },
    { name: 'X', icon: XIcon, href: 'https://twitter.com/lazismu-ummi' },
    { name: 'YouTube', icon: YouTubeIcon, href: 'https://youtube.com/lazismu-ummi' },
    { name: 'Instagram', icon: InstagramIcon, href: 'https://instagram.com/lazismu-ummi' },
  ];

  return (
    <footer className={styles.footer}>
      <div className="container">
        {/* Main Footer Content */}
        <div className={styles.footerContent}>
          {/* Company Info */}
          <div className={styles.companyInfo}>
            <div className={styles.logo}>
              <img src="/images/lazismu-icon.svg" alt="Lazismu" className={styles.logoImage} />
              <span className={styles.logoText}>Lazismu UMMI</span>
            </div>
            <p className={styles.companyDescription}>
              Lembaga Amil Zakat, Infak, dan Shadaqah Muhammadiyah yang berkomitmen
              untuk mengelola dan menyalurkan dana zakat, infak, dan shadaqah secara
              profesional, transparan, dan akuntabel.
            </p>

            {/* Contact Info */}
            <div className={styles.contactInfo}>
              <div className={styles.contactItem}>
                <MapPinIcon className={styles.contactIcon} />
                <span>Universitas Muhammadiyah Sukabumi. Jl. R. Syamsudin, SH. No. 50. Kota Sukabumi</span>
              </div>
              <div className={styles.contactItem}>
                <PhoneIcon className={styles.contactIcon} />
                <a
                  href="https://wa.me/6281563355535"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.contactLink}
                >
                  +62 815-6335-5535
                </a>
              </div>
              <div className={styles.contactItem}>
                <EnvelopeIcon className={styles.contactIcon} />
                <span>lazismu@ummi.ac.id</span>
              </div>
            </div>
          </div>

          {/* Footer Links */}
          <div className={styles.footerLinks}>
            <div className={styles.linkGroup}>
              <h3 className={styles.linkGroupTitle}>Program</h3>
              <ul className={styles.linkList}>
                {footerLinks.program.map((link) => (
                  <li key={link.name}>
                    <Link to={link.href} className={styles.footerLink}>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.linkGroup}>
              <h3 className={styles.linkGroupTitle}>Perusahaan</h3>
              <ul className={styles.linkList}>
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    <Link to={link.href} className={styles.footerLink}>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.linkGroup}>
              <h3 className={styles.linkGroupTitle}>Dukungan</h3>
              <ul className={styles.linkList}>
                {footerLinks.support.map((link) => (
                  <li key={link.name}>
                    <Link to={link.href} className={styles.footerLink}>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className={styles.bottomFooter}>
          <div className={styles.copyright}>
            <p>&copy; {currentYear} LAZISMU UMMI. Semua hak dilindungi.</p>
            <p className={styles.version}>Versi aplikasi {appVersion}</p>
          </div>

          <div className={styles.socialLinks}>
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
                aria-label={social.name}
              >
                <social.icon className={styles.socialIcon} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
